/**
 * WorkflowStages — Patch 34.1.14.
 *
 * Lightweight progress header that answers "where am I in the
 * founder workflow?" on every department page. Ticks the completed
 * stages, highlights the current one, and hints at what's next. Never
 * a blocker — pure orientation.
 *
 * Stages: Business Brain → Campaign → Generation → Review → Vault →
 * Publishing → Analytics.
 */
import { Link } from "react-router-dom";
import { Check } from "lucide-react";

export type WorkflowStage =
  | "brain"
  | "campaign"
  | "generation"
  | "review"
  | "vault"
  | "publishing"
  | "analytics";

const STAGES: Array<{ id: WorkflowStage; label: string; to: string }> = [
  { id: "brain",      label: "Business Brain", to: "/brand-brain" },
  { id: "campaign",   label: "Campaign",       to: "/campaigns" },
  { id: "generation", label: "Generation",     to: "/campaigns" },
  { id: "review",     label: "Review",         to: "/campaigns/review" },
  { id: "vault",      label: "Vault",          to: "/vault" },
  { id: "publishing", label: "Publishing",     to: "/publishing" },
  { id: "analytics",  label: "Analytics",      to: "/insights" },
];

interface Props {
  current: WorkflowStage;
}

export function WorkflowStages({ current }: Props): JSX.Element {
  const currentIndex = STAGES.findIndex((s) => s.id === current);
  return (
    <nav
      data-testid="workflow-stages"
      aria-label="Founder workflow stages"
      className="mb-6 flex flex-wrap items-center gap-2 overflow-x-auto rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)]/40 px-3 py-2 text-xs"
    >
      {STAGES.map((s, i) => {
        const isDone = i < currentIndex;
        const isCurrent = i === currentIndex;
        const isFuture = i > currentIndex;
        return (
          <div key={s.id} className="flex items-center gap-2" data-testid={`workflow-stage-${s.id}`}>
            <Link
              to={s.to}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 transition ${
                isCurrent
                  ? "bg-[var(--md-amber,#e5b454)]/20 text-[var(--md-amber,#e5b454)] font-semibold"
                  : isDone
                  ? "text-[var(--md-ivory-0,#e8e6df)] hover:bg-[var(--md-ink-2)]"
                  : "text-[color:var(--md-fog,#a0a4ac)] hover:text-[var(--md-ivory-0)]"
              }`}
              aria-current={isCurrent ? "step" : undefined}
            >
              {isDone && <Check className="h-3 w-3" aria-hidden />}
              {s.label}
            </Link>
            {i < STAGES.length - 1 && (
              <span
                aria-hidden
                className={`h-px w-4 ${isFuture ? "bg-[color:var(--md-line)]" : "bg-[var(--md-amber,#e5b454)]/50"}`}
              />
            )}
          </div>
        );
      })}
    </nav>
  );
}
